import React from 'react';
import {
  Box,
  LinearProgress as MuiLinearProgress,
  styled,
  LinearProgressProps as MuiLinearProgressProps,
} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';

export enum LinearProgressVariant {
  INDETERMINATE = 'indeterminate',
  DETERMINATE = 'determinate',
  BUFFER = 'buffer',
}

export enum LinearProgressAppearance {
  PRIMARY = 'primary',
  BRAND = 'brand',
}

export interface LinearProgressProps
  extends Omit<MuiLinearProgressProps, 'variant'> {
  /**
   * Test ID for testing
   */
  testID?: string;
  /**
   * The variant of the progress bar
   */
  variant: LinearProgressVariant;
  /**
   * The width of the progress bar in pixels (default: 200)
   */
  width?: number;
  /**
   * The height of the progress bar in pixels (default: 4)
   */
  height?: number;
  /**
   * Whether to show the percentage text (only for determinate and buffer variants)
   */
  showPercentage?: boolean;
  /**
   * The appearance variant of the progress bar
   */
  appearance?: LinearProgressAppearance;
  /**
   * Custom track color for the progress bar
   */
  trackColor?: string;
  /**
   * Custom background color for the progress bar
   */
  backgroundColor?: string;
}

const Container = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  overflow: 'hidden',
  borderRadius: '1000px',
  position: 'relative',
}));

const ProgressContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '4px',
  width: '200px',
  overflow: 'hidden',
  position: 'relative',
}));

const shouldNotForwardProps = ['appearance', 'trackColor', 'backgroundColor'];

const StyledLinearProgress = styled(MuiLinearProgress, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{
  appearance?: LinearProgressAppearance;
  trackColor?: string;
  backgroundColor?: string;
}>(({
  appearance = LinearProgressAppearance.PRIMARY,
  trackColor,
  backgroundColor,
}) => {
  // Color mapping for appearance options
  const getAppearanceColors = (app: LinearProgressAppearance) => {
    switch (app) {
      case LinearProgressAppearance.BRAND:
        return {
          barColor: designSystemToken('semantic.fg.brand'),
          bgColor: designSystemToken('semantic.bg.brand-secondary'),
        };
      case LinearProgressAppearance.PRIMARY:
      default:
        return {
          barColor: designSystemToken('semantic.fg.primary'),
          bgColor: designSystemToken('semantic.bg.neutral-secondary'),
        };
    }
  };

  const appearanceColors = getAppearanceColors(appearance);

  return {
    'height': '4px',
    'width': '100%',
    'backgroundColor': backgroundColor || appearanceColors.bgColor,
    'borderRadius': '1000px',
    '& .MuiLinearProgress-bar': {
      backgroundColor: trackColor || appearanceColors.barColor,
      borderRadius: '1000px',
    },
    '& .MuiLinearProgress-buffer': {
      backgroundColor: backgroundColor || appearanceColors.bgColor,
      borderRadius: '1000px',
    },
  };
});

const ProgressText = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  width: '35px',
  flexShrink: 0,
}));

const PercentageText = styled('p')(() => ({
  fontFamily: 'Brown LL, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '18px',
  letterSpacing: '-0.25px',
  color: designSystemToken('semantic.fg.primary'),
  margin: 0,
  whiteSpace: 'nowrap',
}));

function LinearProgress({
  testID = 'linear-progress',
  width = 200,
  height = 4,
  showPercentage = true,
  appearance = LinearProgressAppearance.PRIMARY,
  trackColor,
  backgroundColor,
  sx,
  ...muiProps
}: LinearProgressProps) {
  const shouldShowPercentage =
    showPercentage &&
    (muiProps.variant === LinearProgressVariant.DETERMINATE ||
      muiProps.variant === LinearProgressVariant.BUFFER);
  const roundedPercentage = muiProps.value ? Math.round(muiProps.value) : 0;

  return (
    <Container data-testid={testID} sx={{width: `${width}px`, ...sx}}>
      <ProgressContainer sx={{width: `${width}px`, height: `${height}px`}}>
        <StyledLinearProgress
          {...muiProps}
          appearance={appearance}
          trackColor={trackColor}
          backgroundColor={backgroundColor}
          sx={{height: `${height}px`}}
        />
      </ProgressContainer>
      {shouldShowPercentage && (
        <ProgressText>
          <PercentageText>{roundedPercentage}%</PercentageText>
        </ProgressText>
      )}
    </Container>
  );
}

export default LinearProgress;
